import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Target, 
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  BarChart3,
  MoreHorizontal,
  ArrowLeft,
  Settings,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
//import { useToast } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ResourceAllocationService from '../../services/resourceAllocationService';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';

const ResourceAllocation = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [resourceData, setResourceData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  // ActionDropdown component - consistent with ComplianceManagement
  const ActionDropdown = ({ resource, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 180);
    }
    setIsOpen((v) => !v);
  };

  const handleView = () => {
    setIsOpen(false);
    resource && (resource.id || resource._id) && onView(resource);
  };

  const handleEdit = () => {
    setIsOpen(false);
    resource && (resource.id || resource._id) && onEdit(resource);
  };

  const handleDelete = () => {
    setIsOpen(false);
    resource && (resource.id || resource._id) && onDelete(resource);
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && rect && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            top: openUpward
              ? window.innerHeight - rect.top + 8
              : rect.bottom + 8,
            left: rect.left,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={handleView}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Details
              </button>
            </li>

            <li>
              <button
                onClick={handleEdit}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                onClick={handleDelete}
                className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
    );
  };

  // Transform backend data to frontend format
  const transformAllocationToDisplay = (allocation) => {
    const utilizationRate = allocation.allocation?.utilizationRate || 0;
    const availableCapacity = allocation.allocation?.availableCapacity || 0;
    const totalCapacity = allocation.allocation?.totalCapacity || 0;
    
    // Determine status based on utilization
    let status = 'Available';
    if (utilizationRate >= 100) {
      status = 'Fully Allocated';
    } else if (utilizationRate >= 80) {
      status = 'Limited';
    } else if (availableCapacity === 0) {
      status = 'Unavailable';
    }
    
    return {
      id: allocation._id || allocation.id,
      allocationId: allocation.allocationId,
      subcontractor: allocation.subcontractor?.name || 'N/A',
      contactPerson: allocation.subcontractor?.contactPerson || 'N/A',
      category: allocation.allocationType || 'N/A',
      totalCapacity: totalCapacity,
      allocatedCapacity: allocation.allocation?.allocatedCapacity || 0,
      availableCapacity: availableCapacity,
      utilizationRate: utilizationRate,
      currentProjects: 0, // Can be calculated from project references
      upcomingProjects: 0, // Can be calculated from project references
      status: status,
      location: allocation.location?.primaryLocation || 'N/A',
      skills: allocation.requirements?.requiredSkills || [],
      hourlyRate: allocation.costs?.hourlyRate || 0,
      lastAllocation: allocation.startDate ? new Date(allocation.startDate).toISOString().split('T')[0] : '',
      nextAvailable: allocation.endDate ? new Date(allocation.endDate).toISOString().split('T')[0] : ''
    };
  };

  useEffect(() => {
    loadResourceData();
  }, [pagination.page, pagination.itemsPerPage, searchTerm, statusFilter, categoryFilter]);

  const loadResourceData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        status: statusFilter,
        allocationType: categoryFilter
      };
      const response = await ResourceAllocationService.getResourceAllocations(params);
      if (response && response.success) {
        const transformedData = response.data.map(transformAllocationToDisplay);
        setResourceData(transformedData);
        setPagination(prev => ({
          ...prev,
          totalItems: response.pagination.total,
          pages: response.pagination.pages,
          current: response.pagination.current,
          limit: response.pagination.limit
        }));
        success('Resource allocation data loaded successfully!');
      } else {
        showError(response?.message || 'Failed to load resource allocation data.');
      }
    } catch (err) {
      console.error('Error loading resource allocation data:', err);
      showError(err.message || 'Failed to load resource allocation data.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadResourceData();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/resource-allocation';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh resource data',
      disabled: loading
    },
    {
      label: 'Allocate Resources',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/resources/create'),
      variant: 'primary',
      tooltip: 'Allocate new resources'
    }
  ];

  const handleView = (resource) => {
    navigate(`/subcontracting/resources/view/${resource.id || resource._id}`);
  };

  const handleEdit = (resource) => {
    navigate(`/subcontracting/resources/edit/${resource.id || resource._id}`);
  };

  const handleDelete = (resource) => {
    setResourceToDelete(resource);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!resourceToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await ResourceAllocationService.deleteResourceAllocation(resourceToDelete.id || resourceToDelete._id);
      if (response && response.success) {
        success('Resource allocation deleted successfully!');
        loadResourceData();
      } else {
        showError(response?.message || 'Failed to delete resource allocation.');
      }
    } catch (err) {
      console.error('Error deleting resource allocation:', err);
      showError(err.message || 'Failed to delete resource allocation.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setResourceToDelete(null);
    }
  };

  const getProjectPill = (label, type, value = null) => {
  let config = { color: '#6b7280', bg: '#f3f4f6' };

  /* STATUS mapping */
  if (type === 'status') {
    const statusMap = {
      'Available':        { color: '#10b981', bg: '#d1fae5' },
      'Limited':          { color: '#f59e0b', bg: '#fef3c7' },
      'Fully Allocated':  { color: '#ef4444', bg: '#fee2e2' },
      'Unavailable':      { color: '#6b7280', bg: '#f3f4f6' }
    };
    config = statusMap[label] || config;
  }

  /* UTILIZATION mapping (by percentage) */
  if (type === 'utilization' && typeof value === 'number') {
    if (value >= 90) {
      label = 'High';
      config = { color: '#ef4444', bg: '#fee2e2' };
    } else if (value >= 70) {
      label = 'Medium';
      config = { color: '#f59e0b', bg: '#fef3c7' };
    } else if (value >= 50) {
      label = 'Low';
      config = { color: '#3b82f6', bg: '#dbeafe' };
    } else {
      label = 'Very Low';
      config = { color: '#10b981', bg: '#d1fae5' };
    }
  }

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
        whiteSpace: 'nowrap'
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {label}
    </span>
  );
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'subcontractor',
      label: 'Subcontractor',
      render: (value, resource) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={resource.subcontractor}>
            {resource.subcontractor}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '150px' }} title={resource.contactPerson}>
            {resource.contactPerson}
          </small>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      render: (value, resource) => (
        <span className="badge bg-light text-dark text-truncate d-inline-block" style={{ maxWidth: '100px' }} title={resource.category}>
          {resource.category}
        </span>
      )
    },
    {
      key: 'capacity',
      label: 'Capacity',
      render: (value, resource) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '120px' }} title={`${resource.allocatedCapacity}/${resource.totalCapacity}`}>
            {resource.allocatedCapacity}/{resource.totalCapacity}
          </div>
          <small className="text-muted">Allocated/Total</small>
        </div>
      )
    },
    {
      key: 'utilizationRate',
      label: 'Utilization',
      render: (value, resource) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={`${resource.utilizationRate}%`}>
            {resource.utilizationRate}%
          </div>
          {getProjectPill('', 'utilization', resource.utilizationRate)}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, resource) => getProjectPill(resource.status, 'status')
    },
    {
      key: 'currentProjects',
      label: 'Projects',
      render: (value, resource) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={resource.currentProjects}>
            {resource.currentProjects}
          </div>
          <small className="text-muted">Current</small>
        </div>
      )
    },
    {
      key: 'hourlyRate',
      label: 'Rate',
      render: (value, resource) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={`$${resource.hourlyRate}/hr`}>
            ${resource.hourlyRate}/hr
          </div>
          <small className="text-muted">Hourly</small>
        </div>
      )
    },
    {
      key: 'nextAvailable',
      label: 'Next Available',
      render: (value, resource) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '120px' }} title={new Date(resource.nextAvailable).toLocaleDateString()}>
            {new Date(resource.nextAvailable).toLocaleDateString()}
          </div>
          <small className="text-muted">Date</small>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, resource) => {
        if (!resource || !resource.id) {
          console.error('Invalid resource data in render function:', resource);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            resource={resource}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Resource Allocation"
        description="Optimize resource allocation and capacity planning for subcontractors"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Resource Allocation Table */}
      <DataTable
        data={resourceData}
        columns={columns}
        title="Resource Allocation"
        loading={loading}
        pagination={pagination}
        onPaginationChange={setPagination}
        showActions={false}
        emptyMessage="No resource allocations found. Click 'Allocate Resources' to get started."
      />
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setResourceToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Resource Allocation"
        message={`Are you sure you want to delete resource allocation ${resourceToDelete?.allocationId || ''} for ${resourceToDelete?.subcontractor || ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
      <ToastContainer />
    </>
  );
};

export default ResourceAllocation;
